import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Instagram, Facebook, Loader2 } from 'lucide-react';
import { siteInfo } from '../data/mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l&#39;envoi du message. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Hero */}
      <section className="relative py-24 bg-[#2C1810]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Nous Contacter</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mt-4 mb-6">
            Restons en Contact
          </h1>
          <div className="w-20 h-1 bg-gradient-to-r from-[#C9A96E] to-[#A67B5B] mx-auto mb-6" />
          <p className="text-[#D4C4B5] text-lg max-w-2xl mx-auto">
            Une question ? Une demande de renseignement ? N&#39;hésitez pas à nous contacter.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-serif font-bold text-[#2C1810] mb-8">
                Informations de contact
              </h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#C9A96E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1810] mb-1">Adresse</h3>
                    <p className="text-[#5D4037]">{siteInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#C9A96E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1810] mb-1">Téléphone</h3>
                    <a href={`tel:${siteInfo.phone}`} className="text-[#5D4037] hover:text-[#C9A96E]">
                      {siteInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#C9A96E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1810] mb-1">Email</h3>
                    <a href={`mailto:${siteInfo.email}`} className="text-[#5D4037] hover:text-[#C9A96E]">
                      {siteInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#C9A96E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1810] mb-1">Horaires d&#39;ouverture</h3>
                    <div className="text-[#5D4037] space-y-1">
                      <p>Lundi : {siteInfo.hours.weekdays}</p>
                      <p className="text-red-600 font-medium">Mardi - Mercredi : Fermé</p>
                      <p>Jeudi - Vendredi : {siteInfo.hours.weekdays}</p>
                      <p>Samedi : {siteInfo.hours.saturday}</p>
                      <p>Dimanche : {siteInfo.hours.sunday}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-semibold text-[#2C1810] mb-4">Suivez-nous</h3>
                <div className="flex gap-4">
                  <a
                    href={siteInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#C9A96E] flex items-center justify-center text-white hover:bg-[#A67B5B] transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram size={24} />
                  </a>
                  <a
                    href={siteInfo.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#C9A96E] flex items-center justify-center text-white hover:bg-[#A67B5B] transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook size={24} />
                  </a>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-10 bg-[#E8DDD4] rounded-2xl h-64 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="mx-auto text-[#C9A96E] mb-2" size={40} />
                  <p className="text-[#5D4037]">Asnières-sur-Seine</p>
                  <p className="text-sm text-[#A67B5B]">Métro: Gabriel Péri (L13)</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl">
              <h2 className="text-3xl font-serif font-bold text-[#2C1810] mb-6">
                Envoyez-nous un message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="border-[#D4C4B5] focus:border-[#C9A96E] focus:ring-[#C9A96E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.com"
                      className="border-[#D4C4B5] focus:border-[#C9A96E] focus:ring-[#C9A96E]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 XX XX XX XX"
                      className="border-[#D4C4B5] focus:border-[#C9A96E] focus:ring-[#C9A96E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Sujet *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Sujet de votre message"
                      className="border-[#D4C4B5] focus:border-[#C9A96E] focus:ring-[#C9A96E]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Votre message..."
                    rows={6}
                    className="border-[#D4C4B5] focus:border-[#C9A96E] focus:ring-[#C9A96E]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C9A96E] hover:bg-[#A67B5B] text-white py-6 text-lg font-semibold rounded-xl"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Envoyer le message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
